import React, { ReactNode } from "react";
import { TableColumn } from "../Table";
import OverflowTooltip from "../OverflowTooltip";
import Pagination2, { Pagination2Props } from "./Pagination2";
import { cn } from "../../../utils/cn";

export type Table2ResponsiveMode = "scroll" | "card" | "auto";

export interface Table2PaginationConfig
  extends Omit<Pagination2Props, "className"> {
  className?: string;
  wrapperClassName?: string;
}

export interface DataTableProps<T extends object> {
  columns: TableColumn<T>[];
  rows: T[];
  rowKey?: (record: T, index: number) => string;
  loading?: boolean;
  fetching?: boolean;
  emptyMessage?: string;
  skeleton?: ReactNode;
  onRowClick?: (record: T, index: number) => void;
  /** Pins the first column to the left (Figma Employees "Employee" column) */
  stickyFirstColumn?: boolean;
  /** "scroll" = horizontal scroll (default), "card" = card list on mobile only */
  responsive?: Table2ResponsiveMode;
  /** Optional integrated pagination configuration */
  pagination?: Table2PaginationConfig;
  className?: string;
  /** Skip the thead strip (Figma row-list panels have no column headers) */
  hideHeader?: boolean;
  /** Minimum table width — forces the overflow scroller on small screens.
   *  Column widths alone cannot do this under table-layout auto. */
  minWidth?: number | string;
}

const HEADER_CELL =
  "text-left text-[11px] font-bold text-[var(--color-text-tertiary)] uppercase tracking-[0.05em] px-[12px] py-[11px]";

const getHideBelowClass = (hideBelow?: "sm" | "md" | "lg"): string => {
  switch (hideBelow) {
    case "sm":
      return "hidden sm:table-cell";
    case "md":
      return "hidden md:table-cell";
    case "lg":
      return "hidden lg:table-cell";
    default:
      return "";
  }
};

const DataTable = <T extends object>({
  columns,
  rows,
  rowKey,
  loading = false,
  fetching = false,
  emptyMessage = "No data available",
  skeleton,
  onRowClick,
  stickyFirstColumn = false,
  responsive = "scroll",
  pagination,
  className = "",
  hideHeader = false,
  minWidth,
}: DataTableProps<T>) => {
  const getRowKey = (record: T, index: number): string => {
    if (rowKey) return rowKey(record, index);
    const candidate = (record as any)?.id || (record as any)?._id;
    if (candidate != null) return String(candidate);
    return index.toString();
  };

  const renderCell = (
    column: TableColumn<T>,
    record: T,
    index: number,
  ): ReactNode => {
    let content: ReactNode = "";
    if (column.render) {
      const val = column.dataIndex ? (record as any)[column.dataIndex] : undefined;
      content = column.render(val, record, index);
    } else if (column.dataIndex) {
      const value = (record as any)[column.dataIndex];
      content = value?.toString() || "";
    }

    if (column.wrap) {
      return <span className="whitespace-normal break-words">{content}</span>;
    }

    if (column.truncate !== false && typeof content === "string") {
      return (
        <OverflowTooltip className="truncate" align={column.align}>
          {content}
        </OverflowTooltip>
      );
    }

    return content;
  };

  const alignClassName = (align?: "left" | "center" | "right") => {
    switch (align) {
      case "center":
        return "text-center";
      case "right":
        return "text-right";
      default:
        return "text-left";
    }
  };

  const bodyLoading = loading || fetching;
  const cardMode = responsive === "card";

  const shouldShowPagination =
    Boolean(pagination) &&
    (pagination?.autoHide === false ||
      (pagination?.total || 0) > (pagination?.pageSize || 0));

  return (
    <div className={className}>
      {cardMode && (
        <div className="block md:hidden divide-y divide-[var(--color-border-tertiary)]">
          {!bodyLoading &&
            rows.map((record, index) => (
              <div
                key={getRowKey(record, index)}
                className={`px-4 py-3 ${onRowClick ? "cursor-pointer" : ""}`}
                onClick={() => onRowClick?.(record, index)}
              >
                {columns.map((column) => {
                  const label =
                    typeof column.title === "string" ? column.title : column.key;
                  return (
                    <div
                      key={`card-${column.key}`}
                      className="flex items-center justify-between py-1.5 gap-2"
                    >
                      <span className="text-xs font-medium text-[var(--color-text-tertiary)] uppercase tracking-wider shrink-0 min-w-[90px]">
                        {label}
                      </span>
                      <span className="text-sm text-[var(--color-text-primary)] text-right">
                        {renderCell(column, record, index)}
                      </span>
                    </div>
                  );
                })}
              </div>
            ))}
        </div>
      )}

      <div className={cardMode ? "hidden md:block" : ""}>
        <div className="relative overflow-x-auto">
          <table
            className="w-full border-collapse"
            style={minWidth ? { minWidth } : undefined}
          >
            {!hideHeader && (
            <thead>
              <tr className="bg-[var(--color-bg-hover)]">
                {columns.map((column, idx) => {
                  const sticky = stickyFirstColumn && idx === 0;
                  const firstCell = idx === 0;
                  const lastCell = idx === columns.length - 1;
                  return (
                    <th
                      key={column.key}
                      scope="col"
                      className={cn(
                        HEADER_CELL,
                        firstCell && "px-[22px]",
                        lastCell && "px-[22px]",
                        alignClassName(column.align),
                        getHideBelowClass(column.hideBelow),
                        sticky && "sticky left-0 z-10 bg-[var(--color-bg-hover)]",
                        column.className
                      )}
                      style={sticky ? undefined : { width: column.width, maxWidth: column.width }}
                    >
                      {typeof column.title === "string" ? (
                        <OverflowTooltip className="truncate uppercase" align={column.align}>
                          {column.title}
                        </OverflowTooltip>
                      ) : (
                        column.title
                      )}
                    </th>
                  );
                })}
              </tr>
            </thead>
            )}
            <tbody>
              {bodyLoading && skeleton ? (
                <tr>
                  <td colSpan={columns.length} className="p-0 border-0">
                    {skeleton}
                  </td>
                </tr>
              ) : bodyLoading ? (
                <tr>
                  <td colSpan={columns.length} className="p-0 border-0">
                    <div className="flex items-center justify-center py-10">
                      <div className="h-8 w-8 animate-spin rounded-full border-2 border-[var(--color-border-input)] border-t-[var(--color-primary-600)]" />
                    </div>
                  </td>
                </tr>
              ) : rows.length === 0 ? (
                <tr>
                  <td colSpan={columns.length} className="border-0">
                    <div className="flex items-center justify-center text-[var(--color-text-secondary)] text-sm py-10">
                      {emptyMessage}
                    </div>
                  </td>
                </tr>
              ) : (
                rows.map((record, index) => (
                  <tr
                    key={getRowKey(record, index)}
                    onClick={() => onRowClick?.(record, index)}
                    className={cn(
                      "hover:bg-[var(--color-bg-hover)] transition-colors duration-150",
                      // With no thead, the first row needs no top divider
                      (!hideHeader || index > 0) &&
                        "border-t border-[var(--color-border-tertiary)]",
                      onRowClick && "cursor-pointer"
                    )}
                  >
                    {columns.map((column, idx) => {
                      const sticky = stickyFirstColumn && idx === 0;
                      const firstCell = idx === 0;
                      const lastCell = idx === columns.length - 1;
                      return (
                        <td
                          key={`${getRowKey(record, index)}-${column.key}`}
                          onClick={
                            column.render && onRowClick
                              ? (e) => e.stopPropagation()
                              : undefined
                          }
                          className={cn(
                            alignClassName(column.align),
                            getHideBelowClass(column.hideBelow),
                            firstCell || lastCell
                              ? "px-[22px] py-[13px]"
                              : "px-[12px] py-[13px]",
                            sticky && "sticky left-0 z-10 bg-[var(--color-bg-base)]",
                            column.className
                          )}
                          style={sticky ? undefined : { width: column.width, maxWidth: column.width }}
                        >
                          {renderCell(column, record, index)}
                        </td>
                      );
                    })}
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {shouldShowPagination && pagination && (
        <div
          className={cn(
            "px-5 py-4 border-t border-[var(--color-border-tertiary)]",
            pagination.wrapperClassName
          )}
        >
          <Pagination2
            total={pagination.total}
            page={pagination.page}
            pageSize={pagination.pageSize}
            pageSizeOptions={pagination.pageSizeOptions}
            onPageChange={pagination.onPageChange}
            onPageSizeChange={pagination.onPageSizeChange}
            autoHide={false}
            className={pagination.className}
          />
        </div>
      )}
    </div>
  );
};

export default DataTable;